<template>
  <div>
    <v-btn
      class="flat-button border"
      id="set-similarity-options-button"
      aria-label="Set similarity options"
      ref="optionsButton"
    >
      <v-icon>mdi-tune</v-icon>
      <v-badge
        v-if="this.isDifferentFromDefault()"
        color="rgb(185, 0, 0)"
        dot
        overlap
        offset-x="-5"
        offset-y="15"
      />
    </v-btn>

    <v-tooltip
      top
      v-bind="defaultVTooltipProps"
      activator="#set-similarity-options-button"
    >
      Set similarity options
    </v-tooltip>

    <v-menu
      activator="#set-similarity-options-button"
      v-model="popoverShow"
      v-bind="defaultVTooltipProps"
      :close-on-content-click="false"
      offset-y
      nudge-bottom="20"
      bottom
      min-width="23rem"
      @input="(val) => handleMenuOpenStatus(val)"
    >
      <v-card>
        <v-card-subtitle class="bg-light font-weight-bold py-2">
          <div
            class="w-100 d-flex flex-row align-items-center justify-content-between"
            style="gap: 1rem"
          >
            <span>Similarity Options</span>
            <v-btn
              @click="onClose"
              class="border-0"
              icon
              small
              aria-label="Close similarity options menu"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-subtitle>

        <v-card-text>
          <b-form-group
            label="Similarity"
            label-for="min-similarity-input"
            label-cols="3"
            class="mb-1 sim-label"
            description="Select Similarity Threshold"
          >
            <b-form-input
              v-model="minSimilarity"
              id="min-similarity-input"
              ref="minSimilarityRef"
              max="1"
              min="0.0"
              step="0.01"
              type="range"
              title="Similarity threshold"
              v-b-tooltip.noninteractive.hover
              class="ml-2"
              style="max-width: 15em; margin-left: 0 !important"
            />
            <span class="min-similarity-display">{{ minSimilarityFormatted }}</span>
          </b-form-group>

          <b-form-group
            label="Min. No."
            label-for="min-num-input"
            label-cols="3"
            class="mb-1 sim-label"
            description="Select Minimum number of structures"
          >
            <input
              id="min-num-input"
              v-model="minNumOfStructures"
              class="form-control"
              type="number"
              min="1"
              title="Minimum number of structures to be returned by the similarity search"
              v-b-tooltip.noninteractive.hover
              oninput="this.value = !!this.value && Math.abs(this.value) > 0 ? Math.abs(this.value) : null"
            />
          </b-form-group>

          <b-form-group
            label="Max. No."
            label-for="max-num-input"
            label-cols="3"
            class="mb-2 sim-label"
            description="Select Maximum number of structures"
          >
            <input
              id="max-num-input"
              v-model="maxNumOfStructures"
              class="form-control"
              type="number"
              min="1"
              title="Maximum number of structures to be returned by the similarity search"
              v-b-tooltip.noninteractive.hover
              oninput="this.value = !!this.value && Math.abs(this.value) > 0 ? Math.abs(this.value) : null"
            />
          </b-form-group>

          <b-form-group
            label="Fingerprints"
            label-for="min-fingerprints-input"
            label-cols="3"
            class="mb-2 sim-label"
            description="Select Fingerprint type"
          >
            <b-form-select
              id="min-fingerprints-input"
              v-model="fingerprints"
              :options="fpTypes"
              size="sm"
            />
          </b-form-group>

          <b-form-group
            label="Metric"
            label-for="min-metric-input"
            label-cols="3"
            class="mb-2 sim-label"
            description="Select Metric"
          >
            <b-form-select
              id="min-metric-input"
              v-model="metric"
              :options="metricOptions"
              size="sm"
            />
          </b-form-group>

          <b-form-group
            label="Authority"
            label-for="min-authority-input"
            label-cols="3"
            class="mb-2 sim-label"
            description="Select Minimum Authority"
          >
            <b-form-select
              id="min-authority-input"
              v-model="authority"
              :options="authorityOptions"
              size="sm"
            />
          </b-form-group>

          <b-form-group
            label="Hazard Name"
            label-for="hazard-name-input"
            label-cols="3"
            class="mb-2 sim-label"
            description="Select Hazard Name"
          >
            <b-form-select
              id="hazard-name-input"
              v-model="hazardName"
              :options="hazardNameOptions"
              size="sm"
            />
          </b-form-group>

          <div style="text-align: right">
            <v-btn
              @click="onReset"
              style="float: left"
              size="sm"
              class="btn-window"
              title="Reset all settings to default"
              v-b-tooltip.noninteractive.hover
              aria-label="Reset all settings to default"
            >
              Reset
            </v-btn>

            <v-btn
              @click="onClose"
              size="sm"
              class="btn-window"
              aria-label="Cancel"
            >
              Cancel
            </v-btn>
            <v-btn
              @click="onOk"
              size="sm"
              class="btn-window theme--light"
              aria-label="Ok"
            >
              Ok
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { defaultVTooltipProps } from '@/utils/defaultProps'

export default {
  data() {
    return {
      input1: '',
      input1state: null,
      input2: '',
      input2state: null,
      input1Return: '',
      input2Return: '',
      popoverShow: false,

      minSimilarity: 0.95,
      maxNumOfStructures: null,
      minNumOfStructures: null,
      fingerprints: 'bingo',
      metric: 'tanimoto',
      authority: null,
      hazardName: null,
      metricOptions: [
        { value: 'tanimoto', text: 'Tanimoto' },
        { value: 'euclid-sub', text: 'Euclidean' },
        { value: 'tversky', text: 'Tversky' },
      ],
      authorityOptions: [
        { text: 'Authoritative', value: 'Authoritative' },
        { text: 'Screening', value: 'Screening' },
        { text: 'QSAR Model', value: 'QSAR Model' },
        { text: '', value: null },
      ],
      defaultVTooltipProps,
    }
  },
  props: {
    options: {
      type: Object,
      required: true,
    },
    defaultOptions: {
      type: Object,
      required: true,
    },
    fpTypes: {
      type: Array,
      required: false,
    },
    hazardNameOptions: {
      type: Array,
      required: false,
    },
  },
  watch: {
    input1(val) {
      if (val) {
        this.input1state = true
      }
    },
    input2(val) {
      if (val) {
        this.input2state = true
      }
    },
  },
  methods: {
    onClose() {
      this.popoverShow = false
    },

    onOk() {
      this.onClose()
      this.minSimilarity = parseFloat(this.minSimilarity)
      this.options.minSimilarity = this.minSimilarity
      if (!this.minNumOfStructures) {
        this.minNumOfStructures = null
      }
      if (!this.maxNumOfStructures) {
        this.maxNumOfStructures = null
      }
      this.options.minNumber = this.minNumOfStructures
      this.options.maxNumber = this.maxNumOfStructures
      this.options.fingerprintType = this.fingerprints
      this.options.similarityType = this.metric
      this.options.authority = this.authority
      this.options.hazardName = this.hazardName
    },
    onReset() {
      this.minSimilarity = this.defaultOptions.minSimilarity
      this.minNumOfStructures = this.defaultOptions.minNumber
      this.maxNumOfStructures = this.defaultOptions.maxNumber
      this.fingerprints = this.defaultOptions.fingerprintType
      this.metric = this.defaultOptions.similarityType
      this.hazardName = this.defaultOptions.hazardName
    },

    onShow() {
      this.minSimilarity = this.options.minSimilarity
      this.minNumOfStructures = this.options.minNumber
      this.maxNumOfStructures = this.options.maxNumber
      this.fingerprints = this.options.fingerprintType
      this.metric = this.options.similarityType
      this.authority = this.options.authority
      this.hazardName = this.options.hazardName
    },

    onHidden() {
      this.focusRef(this.$refs.optionsButton)
    },

    focusRef(ref) {
      const el = ref?.$el || ref
      if (!el) return
      if (typeof el.focus === 'function') requestAnimationFrame(() => el.focus())
    },

    handleMenuOpenStatus(val) {
      if (val) {
        this.onShow()
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.focusRef(this.$refs.minSimilarityRef)
          })
        })
      } else {
        this.onHidden()
      }
    },

    isDifferentFromDefault() {
      const optionsCopy = { ...this.options }
      for (const key in optionsCopy) {
        if (Object.prototype.hasOwnProperty.call(optionsCopy, key)) {
          if (!Object.prototype.hasOwnProperty.call(this.defaultOptions, key)) {
            delete optionsCopy[key]
          }
        }
      }
      return JSON.stringify(optionsCopy) !== JSON.stringify(this.defaultOptions)
    },
  },
  computed: {
    minSimilarityFormatted() {
      return parseFloat(this.minSimilarity).toFixed(2)
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-window {
  margin: 5px;
  text-transform: none;
  min-width: 32px;
}

.close {
  line-height: 0.8 !important;
}

span.min-similarity-display {
  padding-left: 10px;
  vertical-align: super;
}

input {
  height: 28px;
}

.sim-label ::v-deep .col {
  padding-top: 4px;
  padding-bottom: 4px;
}

.form-control {
  padding-left: 0.5rem;
}

.sim-label ::v-deep .form-text {
  margin-top: 0.05rem;
}
</style>
